import React, { useState, useEffect, useCallback } from 'react';
import { auth } from '../firebase';

// ---------------------------------------------------------------------------
// Client Dashboard — users (client profile card)
//
// Who can sign in to dashboard.core6.marketing for this client. Staff invite
// an email here; the person sets their own password from the emailed link
// (7-day expiry, one-click resend). The first user becomes the account owner
// and can invite or deactivate their own team from the dashboard. Everything
// goes through the dashboard codebase's endpoints (functions-dashboard/lib/
// clientApi.js) with the staff Google token + viewAs — the Hub never reads
// or writes these rows directly.
//
// "View as client" opens the dashboard as this client sees it (drafts
// included), clearly labeled, and is logged.
// ---------------------------------------------------------------------------

const BASE = 'https://us-central1-core6-marketing.cloudfunctions.net';
const DASHBOARD = 'https://dashboard.core6.marketing';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

async function api(path, body) {
  const token = await auth.currentUser.getIdToken();
  const r = await fetch(`${BASE}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

const fmt = ms => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

function Badge({ on, children }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      ...(on
        ? { background: 'var(--text-primary)', color: 'var(--bg-primary, #fff)', border: '1px solid var(--text-primary)' }
        : { background: 'transparent', color: 'var(--text-muted)', border: '1px dashed var(--text-muted)' }),
    }}>{on ? '● ' : '○ '}{children}</span>
  );
}

export default function DashboardUsersPanel({ clientId, client }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ users: [], invites: [] });
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try { setData(await api('dashboardUsers', { viewAs: clientId })); }
    catch (err) { setMsg({ ok: false, text: err.message }); }
  }, [clientId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const run = async (label, fn, okText) => {
    setBusy(label);
    setMsg(null);
    try {
      const out = await fn();
      setMsg({ ok: true, text: typeof okText === 'function' ? okText(out) : okText });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Request failed.' });
    } finally {
      setBusy(null);
    }
  };

  const invite = (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) { setMsg({ ok: false, text: 'Enter a valid email address.' }); return; }
    run('invite', () => api('dashboardInvite', { viewAs: clientId, email: clean }), out => {
      setEmail('');
      return out.emailSent ? `Invite sent to ${out.email} as ${out.role}.` : `Invite created for ${out.email}, but the email could not be sent — use Resend.`;
    });
  };

  const active = data.users.filter(u => u.status === 'active').length;

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div className="card-title" style={{ marginBottom: 0 }}>Client Dashboard Users</div>
          {open && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{active} active{data.invites.length ? ` · ${data.invites.length} invited` : ''}</span>}
        </div>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>{open ? '▾ Hide' : '▸ Show'}</span>
      </div>

      {open && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 12 }}>
            People who can sign in to <strong>{DASHBOARD.replace('https://', '')}</strong> and see <strong>{client?.businessName || 'this client'}</strong>'s monthly results.
            The first person invited becomes the owner and can manage their own team. Invites expire after 7 days.
          </div>

          <div style={{ marginBottom: 14 }}>
            <a className="btn btn-secondary" href={`${DASHBOARD}/?viewAs=${encodeURIComponent(clientId)}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
              View as client ↗
            </a>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 10 }}>Sign in there with your Core6 Google account. Drafts are visible; the view is labeled and logged.</span>
          </div>

          <form onSubmit={invite} style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <input value={email} onChange={e => setEmail(e.target.value)} placeholder="client@example.com" style={{ flex: 1, padding: '7px 9px', fontSize: 13, border: '1px solid var(--border-color, #d6d9de)', borderRadius: 6 }} />
            <button type="submit" className="btn btn-primary" disabled={!!busy}>{busy === 'invite' ? 'Sending…' : 'Invite user'}</button>
          </form>

          {msg && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 10 }}>{msg.ok ? '✓ ' : '✕ '}{msg.text}</div>}

          {data.users.map(u => (
            <div key={u.uid} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border-color, #e3e6ea)', flexWrap: 'wrap' }}>
              <Badge on={u.status === 'active'}>{u.status === 'active' ? 'Active' : 'Off'}</Badge>
              <span style={{ fontSize: 13, flex: 1 }}>{u.email} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>· {u.role}{u.lastActiveAt ? ` · last active ${fmt(u.lastActiveAt)}` : ''}</span></span>
              <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={!!busy}
                onClick={() => {
                  const next = u.status === 'active' ? 'disabled' : 'active';
                  if (next === 'disabled' && !window.confirm(`Turn off dashboard access for ${u.email}?`)) return;
                  run(u.uid, () => api('dashboardSetUserStatus', { viewAs: clientId, uid: u.uid, status: next }), `${u.email} is now ${next === 'active' ? 'active' : 'off'}.`);
                }}>
                {u.status === 'active' ? 'Deactivate' : 'Reactivate'}
              </button>
            </div>
          ))}
          {data.invites.map(i => (
            <div key={i.inviteId} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderTop: '1px solid var(--border-color, #e3e6ea)', flexWrap: 'wrap' }}>
              <Badge on={false}>Invited</Badge>
              <span style={{ fontSize: 13, flex: 1 }}>{i.email} <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>· {i.role} · expires {fmt(i.expiresAt)}{i.sentCount > 1 ? ` · sent ${i.sentCount}×` : ''}</span></span>
              <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} disabled={!!busy}
                onClick={() => run(i.inviteId, () => api('dashboardResendInvite', { viewAs: clientId, inviteId: i.inviteId }), `Invite resent to ${i.email}.`)}>
                {busy === i.inviteId ? 'Sending…' : 'Resend'}
              </button>
            </div>
          ))}
          {!data.users.length && !data.invites.length && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No dashboard users yet.</div>}
        </div>
      )}
    </div>
  );
}
